import { Link, Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Reveal from "@/components/Reveal";
import ProjectCard from "@/components/ProjectCard";
import { fetchProjects } from "@/lib/api";
import { usePageMeta } from "@/lib/meta";
import { cityBySlug } from "@/lib/site";

export default function CityPage() {
  const { citySlug } = useParams<{ citySlug: string }>();
  const city = cityBySlug(citySlug);

  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: fetchProjects });
  const cityProjects = projects?.filter((p) => p.city_slug === citySlug) ?? [];

  usePageMeta(
    city
      ? `Turnkey Interior Designers in ${city.name} | Shriya Redkar Interiors`
      : "City | Shriya Redkar Interiors",
    city?.blurb
  );

  if (!city) return <Navigate to="/portfolio" replace />;

  return (
    <main className="pt-[76px]">
      <section className="container-x py-16 lg:py-24">
        <Reveal>
          <p className="eyebrow" data-testid="city-eyebrow">Shriya Redkar Interiors — {city.name}</p>
          <h1 className="mt-5 max-w-4xl font-serif text-4xl font-light leading-[1.08] text-zinc-50 sm:text-5xl lg:text-6xl">
            Turnkey interior design in <span className="italic text-gold">{city.name}.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-zinc-400" data-testid="city-blurb">
            {city.blurb}
          </p>
          <p className="mt-4 font-mono text-xs text-zinc-500" data-testid="city-areas">
            {city.areas}
          </p>
        </Reveal>
      </section>

      <section className="container-x pb-20">
        <Reveal>
          <h2 className="font-serif text-2xl font-light text-zinc-100 sm:text-3xl">
            Delivered in {city.name} — both pictures.
          </h2>
        </Reveal>
        <div className="mt-8 grid gap-6 md:grid-cols-2" data-testid="city-projects">
          {cityProjects.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 2) * 0.08}>
              <div className="aspect-[4/3] w-full">
                <ProjectCard project={p} />
              </div>
            </Reveal>
          ))}
        </div>
        {projects && cityProjects.length === 0 && (
          <p className="mt-8 border border-white/10 bg-obsidian-soft p-10 text-center text-zinc-400" data-testid="city-projects-empty">
            We're between handovers in {city.name} — the next delivery lands here. Ask us about current
            sites on a call.
          </p>
        )}
      </section>

      <section className="border-t border-white/10 bg-obsidian-soft">
        <div className="container-x grid gap-8 py-16 lg:grid-cols-12 lg:py-20">
          <Reveal className="lg:col-span-7">
            <h2 className="font-serif text-3xl font-light leading-tight text-zinc-50 sm:text-4xl">
              Planning a {city.name} home? Start with the same question every client asks.
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-zinc-400">
              "Will it look like the render?" — we answer it with proof, not promises. Book a consultation
              and we'll map your space, budget band and timeline on day one.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="flex items-center lg:col-span-4 lg:col-start-9">
            <Link
              to={`/enquire?city=${encodeURIComponent(city.name)}`}
              data-testid={`city-cta-enquire-${city.slug}`}
              className="btn-gold w-full"
            >
              Enquire about a {city.name} project
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
